import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { PageState } from "@/components/common/PageState";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  downloadServiceDailyRevenueExport,
  downloadServiceDetailExport,
  downloadServiceDiscountImpactExport,
  downloadServiceRelatedReviewsExport,
} from "./components/services/serviceDetailExport";
import { copyAnalyticsDateParams } from "./config/analyticsQueryParams";
import {
  AnalyticsPageProvider,
  useAnalyticsPage,
} from "./context/AnalyticsPageContext";
import { ServiceDetailFiltersBar } from "./components/services/ServiceDetailFiltersBar";
import { ServiceDetailHeader } from "./components/services/ServiceDetailHeader";
import { ServiceDiscountImpactChart } from "./components/services/ServiceDiscountImpactChart";
import { ServicePopularitySection } from "./components/services/ServicePopularitySection";
import { ServiceRelatedReviewsSection } from "./components/services/ServiceRelatedReviewsSection";
import { ServiceWeeklyUsageChart } from "./components/services/ServiceWeeklyUsageChart";
import type { AnalyticsServiceDetailLocationState } from "./components/services/serviceDetailNavigation";
import type { TopServiceRow } from "./components/services/mockData";
import { useAnalyticsServiceDetail } from "./hooks/useAnalyticsServiceDetail";

function buildServicesListBackSearch(searchParams: URLSearchParams): string {
  const params = new URLSearchParams();
  copyAnalyticsDateParams(searchParams, params);
  const segment = searchParams.get("segment");

  if (segment) {
    params.set("segment", segment);
  }

  const page = searchParams.get("page");
  if (page) {
    params.set("page", page);
  }

  return params.toString();
}

function buildDisplayService(
  serviceId: string,
  name: string | undefined,
  navigationService: TopServiceRow | undefined,
): TopServiceRow {
  if (navigationService?.id === serviceId) {
    return navigationService;
  }

  return {
    id: serviceId,
    rate: 0,
    name: name ?? serviceId,
    category: "guestServices",
    bookings: 0,
    guestsUsedPercent: 0,
    revenue: 0,
    availabilityDays: ["monday"],
  };
}

function AnalyticsServiceDetailPageContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const [searchParams] = useSearchParams();
  const { dateRange, setDateRange } = useAnalyticsPage();

  const navigationState =
    location.state as AnalyticsServiceDetailLocationState | null;
  const navigationService =
    navigationState?.service?.id === serviceId
      ? navigationState?.service
      : undefined;

  const detail = useAnalyticsServiceDetail(
    serviceId,
    dateRange,
    navigationService,
  );

  const displayService = useMemo(
    () =>
      serviceId
        ? buildDisplayService(serviceId, detail.serviceName, navigationService)
        : undefined,
    [detail.serviceName, navigationService, serviceId],
  );

  const handleBack = useCallback(() => {
    const search = buildServicesListBackSearch(searchParams);

    navigate({
      pathname: "/analytics/services",
      search,
    });
  }, [navigate, searchParams]);

  const handleExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!displayService || !detail.analytics || !detail.isReady) {
        return;
      }

      try {
        downloadServiceDetailExport(format, {
          serviceId: displayService.id,
          serviceName: displayService.name,
          from: detail.from,
          to: detail.to,
          analytics: detail.analytics,
        });
        toast.success(
          t(
            "analytics.services.detail.exportSuccess",
            "Service analytics export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.services.detail.exportError",
            "Failed to export service analytics",
          ),
        );
      }
    },
    [
      detail.analytics,
      detail.from,
      detail.isReady,
      detail.to,
      displayService,
      t,
    ],
  );

  const handleDailyRevenueExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!displayService || !detail.analytics || !detail.isReady) {
        return;
      }

      try {
        downloadServiceDailyRevenueExport(format, {
          serviceId: displayService.id,
          serviceName: displayService.name,
          from: detail.from,
          to: detail.to,
          dailyRevenue: detail.analytics.dailyRevenue,
        });
        toast.success(
          t(
            "analytics.services.detail.dailyRevenueExportSuccess",
            "Revenue by day export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.services.detail.dailyRevenueExportError",
            "Failed to export revenue by day",
          ),
        );
      }
    },
    [
      detail.analytics,
      detail.from,
      detail.isReady,
      detail.to,
      displayService,
      t,
    ],
  );

  const handleDiscountImpactExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!displayService || !detail.analytics || !detail.isReady) {
        return;
      }

      try {
        downloadServiceDiscountImpactExport(format, {
          serviceId: displayService.id,
          serviceName: displayService.name,
          from: detail.from,
          to: detail.to,
          discountImpact: detail.analytics.discountImpact,
          savedPercent: detail.analytics.discountSavedPercent,
          totalProfit: detail.analytics.discountTotalProfit,
        });
        toast.success(
          t(
            "analytics.services.detail.discountImpactExportSuccess",
            "Discount impact export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.services.detail.discountImpactExportError",
            "Failed to export discount impact",
          ),
        );
      }
    },
    [
      detail.analytics,
      detail.from,
      detail.isReady,
      detail.to,
      displayService,
      t,
    ],
  );

  const handleRelatedReviewsExport = useCallback(
    (format: "csv" | "xlsx") => {
      if (!displayService || !detail.analytics || !detail.isReady) {
        return;
      }

      try {
        downloadServiceRelatedReviewsExport(format, {
          serviceId: displayService.id,
          serviceName: displayService.name,
          from: detail.from,
          to: detail.to,
          reviewSummary: detail.analytics.reviewSummary,
          reviews: detail.analytics.reviews,
        });
        toast.success(
          t(
            "analytics.services.detail.relatedReviewsExportSuccess",
            "Related reviews export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.services.detail.relatedReviewsExportError",
            "Failed to export related reviews",
          ),
        );
      }
    },
    [
      detail.analytics,
      detail.from,
      detail.isReady,
      detail.to,
      displayService,
      t,
    ],
  );

  if (!serviceId) {
    return <Navigate to="/analytics/services" replace />;
  }

  if (detail.isNotFound) {
    return <Navigate to="/analytics/services" replace />;
  }

  if (detail.isError) {
    return (
      <div className="p-6 font-sans">
        <PageState
          text={t(
            "analytics.services.detail.loadError",
            "Failed to load service analytics. Please try again.",
          )}
        />
      </div>
    );
  }

  if (
    detail.isLoading ||
    !displayService ||
    !detail.extras ||
    !detail.analytics
  ) {
    return (
      <div className="p-6 font-sans">
        <PageState
          text={t(
            "analytics.services.detail.loading",
            "Loading service analytics...",
          )}
        />
      </div>
    );
  }

  const { analytics, extras } = detail;

  return (
    <div className="space-y-6 p-6 font-sans">
      <Button
        type="button"
        variant="ghost"
        onClick={handleBack}
        className="h-8 w-fit justify-start self-start rounded-md bg-transparent px-3 py-1 text-base font-normal text-black hover:bg-transparent"
      >
        <Undo2 className="h-6 w-6" />
        {t("analytics.common.back", "Back")}
      </Button>

      <ServiceDetailHeader service={displayService} extras={extras} />

      <ServiceDetailFiltersBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onExport={handleExport}
        isExportDisabled={!detail.analytics}
      />

      <div
        className={cn(
          "space-y-6 transition-opacity duration-150",
          detail.isFetching && "opacity-70",
        )}
      >
        <ServicePopularitySection metrics={analytics.popularity} />

        <ServiceWeeklyUsageChart
          data={analytics.dailyRevenue}
          trendLabel={analytics.revenueTrendLabel}
          trendSubtext={analytics.revenueTrendSubtext}
          highlightDay={analytics.revenueHighlightDay}
          onExport={handleDailyRevenueExport}
          exportDisabled={analytics.dailyRevenue.length === 0}
        />
        <ServiceDiscountImpactChart
          data={analytics.discountImpact}
          savedPercent={analytics.discountSavedPercent}
          totalProfit={analytics.discountTotalProfit}
          onExport={handleDiscountImpactExport}
          exportDisabled={analytics.discountImpact.length === 0}
        />

        <ServiceRelatedReviewsSection
          summary={analytics.reviewSummary}
          reviews={analytics.reviews}
          onExport={handleRelatedReviewsExport}
          exportDisabled={analytics.reviews.length === 0}
        />
      </div>
    </div>
  );
}

export const AnalyticsServiceDetailPage: React.FC = () => {
  return (
    <AnalyticsPageProvider>
      <AnalyticsServiceDetailPageContent />
    </AnalyticsPageProvider>
  );
};
